import { useLingui } from "@lingui/react/macro";
import type { WorkspaceBounds } from "@rakazo/contracts";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  OverlayScope,
  toast,
} from "@rakazo/ui-web";
import { Copy, Maximize2, Minimize2, Minus, Square, X } from "lucide-react";
import type { ReactNode, PointerEvent as ReactPointerEvent } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { OpenWindow } from "../lib/use-workspace-windows";
import { WorkspacePaneWidthContext } from "../lib/workspace-local-state";
import { WorkspaceAppIcon } from "./WorkspaceAppIcon";

type Edge = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

const EDGES: { edge: Edge; className: string; cursor: string }[] = [
  { edge: "n", className: "inset-x-3 top-0 h-1.5", cursor: "ns-resize" },
  { edge: "s", className: "inset-x-3 bottom-0 h-1.5", cursor: "ns-resize" },
  { edge: "w", className: "inset-y-3 left-0 w-1.5", cursor: "ew-resize" },
  { edge: "e", className: "inset-y-3 right-0 w-1.5", cursor: "ew-resize" },
  { edge: "nw", className: "left-0 top-0 h-3 w-3", cursor: "nwse-resize" },
  { edge: "se", className: "bottom-0 right-0 h-4 w-4", cursor: "nwse-resize" },
  { edge: "ne", className: "right-0 top-0 h-3 w-3", cursor: "nesw-resize" },
  { edge: "sw", className: "bottom-0 left-0 h-3 w-3", cursor: "nesw-resize" },
];

function resized(edge: Edge, start: WorkspaceBounds, dx: number, dy: number): WorkspaceBounds {
  const next = { ...start };
  if (edge.includes("e")) next.width = start.width + dx;
  if (edge.includes("s")) next.height = start.height + dy;
  if (edge.includes("w")) {
    next.x = start.x + dx;
    next.width = start.width - dx;
  }
  if (edge.includes("n")) {
    next.y = start.y + dy;
    next.height = start.height - dy;
  }
  return next;
}

export function WorkspaceWindowFrame({
  window: item,
  overlayParent,
  focused,
  index,
  onFocus,
  onMove,
  onState,
  onClose,
  onNewWindow,
  children,
}: {
  window: OpenWindow;
  overlayParent?: HTMLElement | null;
  focused: boolean;
  index: number;
  onFocus: () => void;
  onMove: (bounds: WorkspaceBounds) => void;
  onState: (state: OpenWindow["state"]) => void;
  onClose: () => void;
  onNewWindow?: () => void;
  children: ReactNode;
}) {
  const { t } = useLingui();
  const frame = useRef<HTMLElement>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [width, setWidth] = useState(item.bounds.width - 2);
  useLayoutEffect(() => {
    const element = frame.current;
    if (!element) return;
    const changed = () => setFullscreen(document.fullscreenElement === element);
    // Activity suspends listeners while minimized; fullscreen can end in that interval.
    changed();
    document.addEventListener("fullscreenchange", changed);
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      document.removeEventListener("fullscreenchange", changed);
    };
  }, []);
  useEffect(() => {
    // A link or New window can focus another app while this one owns the top layer.
    // Release fullscreen so the newly focused window is reachable.
    if (!focused && document.fullscreenElement === frame.current)
      void document.exitFullscreen().catch(() => undefined);
  }, [focused]);
  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement === frame.current) await document.exitFullscreen();
      else {
        // Move portal roots before the next interaction can open a modal. Moving an
        // already-open dialog into a previously aria-hidden window hides the dialog too.
        setFullscreen(true);
        await frame.current?.requestFullscreen();
      }
    } catch {
      toast.error(t`Could not enter fullscreen`);
    } finally {
      setFullscreen(Boolean(frame.current && document.fullscreenElement === frame.current));
    }
  }
  async function minimize() {
    if (document.fullscreenElement === frame.current) {
      try {
        await document.exitFullscreen();
        setFullscreen(false);
      } catch {
        return;
      }
    }
    onState("minimized");
  }
  // The window carries the specific name, so two windows of one app are told apart. Its controls
  // keep the app's own name and are distinguished by sitting inside that named window.
  const label = item.title ?? item.name;
  // A window is known by its content and its app, the way a desktop window is, so two windows of
  // one app are distinct to a screen reader. The bar shows just the content; the icon says the app.
  const windowName = item.title ? `${item.title} — ${item.name}` : item.name;
  const drag = useRef<{ pointerId: number; x: number; y: number; start: WorkspaceBounds } | null>(
    null,
  );

  const [manipulating, setManipulating] = useState(false);
  function begin(event: ReactPointerEvent<HTMLElement>, restore = false) {
    if (fullscreen || event.button !== 0 || drag.current) return false;
    event.currentTarget.setPointerCapture(event.pointerId);
    setManipulating(true);
    const bounds =
      restore && item.state === "maximized" && item.restore
        ? {
            ...item.restore,
            x:
              item.bounds.x +
              (event.clientX - event.currentTarget.getBoundingClientRect().left) *
                (1 - item.restore.width / item.bounds.width),
            y: item.bounds.y,
          }
        : item.bounds;
    drag.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      start: bounds,
    };
    return true;
  }
  function end(event: ReactPointerEvent<HTMLElement>) {
    if (drag.current?.pointerId !== event.pointerId) return;
    drag.current = null;
    setManipulating(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  }

  const titleBar = {
    onPointerDown(event: ReactPointerEvent<HTMLElement>) {
      onFocus();
      if (!begin(event, true)) return;
      event.preventDefault();
    },
    onPointerMove(event: ReactPointerEvent<HTMLElement>) {
      const current = drag.current;
      if (current?.pointerId !== event.pointerId) return;
      onMove({
        ...current.start,
        x: current.start.x + (event.clientX - current.x),
        y: current.start.y + (event.clientY - current.y),
      });
    },
    onPointerUp: end,
    onPointerCancel: end,
    onLostPointerCapture: end,
  };

  // Windows use a physical coordinate space, matching the physical pointer deltas that move them.
  return (
    <section
      ref={frame}
      aria-label={windowName}
      data-workspace-window={item.id}
      data-workspace-app={item.app}
      data-window-state={item.state}
      data-focused={focused || undefined}
      data-manipulating={manipulating || undefined}
      className="workspace-window absolute flex flex-col overflow-hidden [&_[data-window-chrome]]:hidden"
      style={{
        left: item.bounds.x,
        top: item.bounds.y,
        width: item.bounds.width,
        height: item.bounds.height,
        zIndex: 20 + index * 2,
      }}
      onPointerDownCapture={onFocus}
    >
      <OverlayScope
        active={item.state !== "minimized"}
        modalActive={focused && item.state !== "minimized"}
        parent={fullscreen ? frame.current : overlayParent}
        modalParent={fullscreen ? frame.current : undefined}
        zIndex={21 + index * 2}
      >
        <ContextMenu>
          <ContextMenuTrigger render={<header className="workspace-window-bar" />}>
            {/* The grip carries the drag, and arrow keys move the window without a pointer. */}
            <button
              type="button"
              className="workspace-window-grip"
              aria-label={t`Move ${item.name}`}
              {...titleBar}
              onDoubleClick={() => {
                if (!fullscreen) onState(item.state === "maximized" ? "normal" : "maximized");
              }}
              onKeyDown={(event) => {
                if (fullscreen) return;
                const step = event.shiftKey ? 1 : 20;
                const delta = {
                  ArrowLeft: { x: -step, y: 0 },
                  ArrowRight: { x: step, y: 0 },
                  ArrowUp: { x: 0, y: -step },
                  ArrowDown: { x: 0, y: step },
                }[event.key];
                if (!delta) return;
                event.preventDefault();
                onMove({ ...item.bounds, x: item.bounds.x + delta.x, y: item.bounds.y + delta.y });
              }}
            >
              <WorkspaceAppIcon id={item.app} compact />
              <span className="workspace-window-title">{label}</span>
            </button>
            <span className="workspace-window-actions">
              {onNewWindow && (
                <button
                  type="button"
                  aria-label={t`New ${item.name} window`}
                  title={t`New window`}
                  onClick={onNewWindow}
                >
                  <Copy size={13} aria-hidden="true" />
                </button>
              )}
              <button
                type="button"
                aria-label={t`Minimize ${item.name}`}
                onClick={() => void minimize()}
              >
                <Minus size={13} aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={
                  item.state === "maximized" ? t`Restore ${item.name}` : t`Maximize ${item.name}`
                }
                disabled={fullscreen}
                aria-pressed={item.state === "maximized"}
                onClick={() => onState(item.state === "maximized" ? "normal" : "maximized")}
              >
                <Square size={11} aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={fullscreen ? t`Exit fullscreen` : t`Enter fullscreen`}
                title={fullscreen ? t`Exit fullscreen` : t`Enter fullscreen`}
                aria-pressed={fullscreen}
                onClick={() => void toggleFullscreen()}
              >
                {fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
              </button>
              <button type="button" aria-label={t`Close ${item.name}`} onClick={onClose}>
                <X size={13} aria-hidden="true" />
              </button>
            </span>
          </ContextMenuTrigger>
          <ContextMenuContent aria-label={t`${label} window actions`}>
            {onNewWindow && (
              <ContextMenuItem onClick={onNewWindow}>{t`New window`}</ContextMenuItem>
            )}
            <ContextMenuItem onClick={() => void minimize()}>{t`Minimize`}</ContextMenuItem>
            <ContextMenuItem
              disabled={fullscreen}
              onClick={() => onState(item.state === "maximized" ? "normal" : "maximized")}
            >
              {item.state === "maximized" ? t`Restore` : t`Maximize`}
            </ContextMenuItem>
            <ContextMenuItem onClick={() => void toggleFullscreen()}>
              {fullscreen ? t`Exit fullscreen` : t`Enter fullscreen`}
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={onClose}>{t`Close window`}</ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
        <WorkspacePaneWidthContext.Provider value={width}>
          <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">{children}</div>
        </WorkspacePaneWidthContext.Provider>
        {!fullscreen &&
          EDGES.map(({ edge, className, cursor }) => (
            <span
              key={edge}
              aria-hidden="true"
              data-resize-edge={edge}
              className={`absolute z-50 touch-none ${className}`}
              style={{ cursor }}
              onPointerDown={(event) => {
                onFocus();
                if (!begin(event)) return;
                event.preventDefault();
              }}
              onPointerMove={(event) => {
                const current = drag.current;
                if (current?.pointerId !== event.pointerId) return;
                onMove(
                  resized(
                    edge,
                    current.start,
                    event.clientX - current.x,
                    event.clientY - current.y,
                  ),
                );
              }}
              onPointerUp={end}
              onPointerCancel={end}
              onLostPointerCapture={end}
            ></span>
          ))}
      </OverlayScope>
    </section>
  );
}
